import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import {
  getSourcexPatientsPath,
  getSourcexTemplatesPath,
} from "../services/firestorePaths";
import {
  ArrowLeft,
  FileText,
  Save,
  CheckCircle2,
  Loader2,
  ListTree,
  ChevronRight,
  Printer,
  Trash2,
} from "lucide-react";
import QuestionField, {
  inputCls as sharedInputCls,
  selectCls as sharedSelectCls,
} from "../components/sourcex/QuestionField";
import ExportSourceDocModal from "../components/sourcex/ExportSourceDocModal";
import { buildBranchDepth, buildLastInRun } from "../components/sourcex/branchLayout";
import {
  normalizePatientData,
  normalizeTemplate,
  visibleQuestions,
  isSourceOnlyType,
  describeCondition,
} from "../services/templateSchema";

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
  </div>
);

// Styling now lives with the shared field renderer so the two SourceX pages
// can't drift apart visually.
const inputCls = sharedInputCls;
const selectCls = sharedSelectCls;

// DOM id for a question/heading block, so the outline can scroll to it. Same
// scheme SectionEditor uses, kept local so the two pages stay independent.
const anchorId = (id) => `sx-p-${id}`;

// Initials from a patient name ("John A. Smith" -> "JAS"), for the export
// header. Capped so an unusually long name can't overflow the field.
const initialsFromName = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .join("")
    .slice(0, 4);

// Fill in DISPLAY-ONLY field structure (table columns, dropdown options, unit
// toggle) from the study template when a patient's snapshot is missing it — the
// reason a table question could render as an empty box. Presentation only: it
// never overwrites a recorded answer or metadata the snapshot already has, and
// nothing is written back to Firestore. Answers stay authoritative on the
// patient doc; only the shape needed to render/fill a field is borrowed.
const hydrateFromTemplate = (q, tq) => {
  if (!tq) return q;
  const out = { ...q };
  if (
    q.type === "Table" &&
    (!Array.isArray(q.columns) || q.columns.length === 0) &&
    Array.isArray(tq.columns) &&
    tq.columns.length
  ) {
    out.columns = tq.columns;
    if (out.minRows == null && tq.minRows != null) out.minRows = tq.minRows;
    if (out.maxRows == null && tq.maxRows != null) out.maxRows = tq.maxRows;
  }
  if (
    (q.type === "Select" || q.type === "MultiSelect") &&
    (!Array.isArray(q.options) || q.options.length === 0) &&
    Array.isArray(tq.options) &&
    tq.options.length
  ) {
    out.options = tq.options;
  }
  if (q.type === "Number" && !q.unit && !q.units) {
    if (Array.isArray(tq.units) && tq.units.length) {
      out.units = tq.units;
      out.unit = tq.unit || tq.units[0];
    } else if (tq.unit) {
      out.unit = tq.unit;
    }
  }
  return out;
};

const hydratePatientData = (data, template) => {
  if (!template) return data;
  const byId = new Map();
  (template.sections || []).forEach((s) =>
    (s.events || []).forEach((e) =>
      (e.questions || []).forEach((q) => byId.set(q.id, q))
    )
  );
  return (data || []).map((s) => ({
    ...s,
    events: (s.events || []).map((e) => ({
      ...e,
      questions: (e.questions || []).map((q) =>
        hydrateFromTemplate(q, byId.get(q.id))
      ),
    })),
  }));
};

// Split a flat (already branch-filtered) question list into heading blocks —
// a Header owns every field until the next Header. Fields before the first
// heading become a leading "ungrouped" group with no collapse toggle.
const buildOutline = (questions = []) => {
  const groups = [];
  let current = { heading: null, items: [] };
  for (const q of questions) {
    if (q.type === "Header") {
      if (current.heading || current.items.length) groups.push(current);
      current = { heading: q, items: [] };
    } else {
      current.items.push(q);
    }
  }
  if (current.heading || current.items.length) groups.push(current);
  return groups;
};

// One patient answer row, memoized so typing in one field doesn't re-render the
// whole form (the perf fix). It re-renders only when its own question object,
// conditional flag, or the (stable) save callbacks change. `onValue`/`onPatch`
// are keyed by question id so the parent can hold a single stable callback.
const PatientQuestionRow = memo(function PatientQuestionRow({
  question,
  conditional,
  depth = 0,
  lastInRun = false,
  conditionHint = "",
  readOnly = false,
  onValue,
  onPatch,
}) {
  const q = question;
  const sourceOnly = isSourceOnlyType(q.type); // Signature: no label row
  // Same visual nesting as the template editor: a field that only appears
  // after a certain answer sits indented under whatever reveals it, joined by
  // a connector, so the form reads as a tree rather than a flat list.
  const nested = !sourceOnly && depth > 0;
  const rail = conditional
    ? "border-l-2 border-amber-400/50 bg-amber-400/[0.04] rounded-r-lg py-1.5"
    : nested
    ? "bg-amber-400/[0.03] rounded-r-lg py-1.5"
    : "border-l border-white/10";
  return (
    <div
      id={anchorId(q.id)}
      style={sourceOnly ? undefined : { marginLeft: `${0.75 + depth * 1.5}rem` }}
      className={
        sourceOnly
          ? "scroll-mt-24 mb-3"
          : `relative scroll-mt-24 mb-3 pl-3 transition-[margin] ${rail}`
      }
    >
      {/* Connector: a trunk starting one row-gap above so it meets the field
          that reveals this one, and an arm on the label's centre line. It
          stops at the elbow on the last field of a run. Not drawn for a
          manually revealed field, which keeps its solid amber rail. */}
      {nested && !conditional && (
        <>
          <span
            aria-hidden="true"
            className="absolute left-0 w-px bg-amber-400/50"
            style={{
              top: "-0.75rem",
              height: lastInRun ? "1.9rem" : "calc(100% + 0.75rem)",
            }}
          />
          <span
            aria-hidden="true"
            className="absolute left-0 h-px w-2.5 bg-amber-400/50"
            style={{ top: "1.15rem" }}
          />
        </>
      )}
      {!sourceOnly && (
        <p className="font-medium text-slate-100 flex items-center gap-2 flex-wrap">
          <span>
            {q.question}
            {q.required && <span className="ml-1 text-rose-300">*</span>}
          </span>
          {/* Marked for EVERY field that has a rule, not only one that had to
              be revealed by hand. A field whose condition is already met is
              just as conditional, and saying so is what tells the coordinator
              this question appeared because of an earlier answer. The tooltip
              names the rule. */}
          {(conditional || nested) && (
            <span
              title={conditionHint || undefined}
              className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                conditional
                  ? "text-amber-200 bg-amber-400/15 border-amber-400/30"
                  : "text-amber-200/80 bg-amber-400/10 border-amber-400/25"
              }`}
            >
              {conditional ? "not triggered" : "conditional"}
            </span>
          )}
        </p>
      )}
      <QuestionField
        question={q}
        value={q.value}
        readOnly={readOnly}
        onChange={(val) => onValue(q.id, val)}
        onQuestionChange={(patch) => onPatch(q.id, patch)}
      />
    </div>
  );
});

// Document outline (Google-Docs style): a sticky, dense index of the current
// visit. Navigation only — it never edits an answer. Collapsed headings keep a
// long form on one screen; expanding one reveals its questions.
function PatientOutline({ questions, expanded, onToggleHeading, onJump, branchDepth }) {
  const groups = useMemo(() => buildOutline(questions), [questions]);
  const headingCount = groups.filter((g) => g.heading).length;

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-2.5">
      <div className="flex items-center gap-1.5 px-1 pb-2 mb-1 border-b border-white/10">
        <ListTree className="w-4 h-4 text-purple-300 shrink-0" />
        <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white">
          Outline
        </span>
        <span className="ml-auto text-[11px] text-slate-500">
          {headingCount || groups.length}
        </span>
      </div>

      {groups.length === 0 ? (
        <p className="px-1 py-2 text-xs text-slate-500">Nothing to outline yet.</p>
      ) : (
        <div className="max-h-[calc(100vh-11rem)] overflow-y-auto scrollbar-thin pr-0.5">
          {groups.map((g, gi) => {
            const key = g.heading ? g.heading.id : `ungrouped-${gi}`;
            const isOpen = expanded.has(key);
            return (
              <div key={key} className="mb-0.5">
                <div
                  role="button"
                  tabIndex={0}
                  className={`group flex items-center gap-0.5 rounded px-1.5 py-1 cursor-pointer transition ${
                    g.heading
                      ? "hover:bg-white/[0.06]"
                      : "hover:bg-white/[0.04] opacity-80"
                  }`}
                  onClick={() => {
                    // The outline drives the document both ways: opening a
                    // section here opens it there and scrolls to it, closing it
                    // here closes it there. Jumping only on OPEN — scrolling to
                    // something you just collapsed is disorienting, and the
                    // jump handler force-expands its target, which would undo
                    // the collapse immediately.
                    const willOpen = !isOpen;
                    onToggleHeading(key, g.heading?.id, willOpen);
                    if (g.heading && willOpen) onJump(g.heading.id);
                  }}
                  title={g.heading ? g.heading.question : "Fields before the first heading"}
                >
                  <ChevronRight
                    className={`w-3.5 h-3.5 shrink-0 text-slate-500 transition-transform ${
                      isOpen ? "rotate-90" : ""
                    }`}
                  />
                  <span
                    className={`flex-1 min-w-0 truncate text-xs leading-tight ${
                      g.heading ? "font-semibold text-slate-100" : "italic text-slate-400"
                    }`}
                  >
                    {g.heading ? g.heading.question || "(untitled heading)" : "Ungrouped fields"}
                  </span>
                </div>

                {isOpen &&
                  g.items.map((q) => {
                    // Same nesting as the document, so the index and the form
                    // read the same shape.
                    const depth = branchDepth?.get(q.id) || 0;
                    return (
                      <div
                        key={q.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => onJump(q.id)}
                        title={
                          depth > 0
                            ? `${q.question || "(untitled)"} — only appears after a certain answer`
                            : q.question
                        }
                        style={{ marginLeft: `${0.75 + depth * 0.6}rem` }}
                        className={`pl-2 border-l rounded px-1.5 py-[3px] cursor-pointer hover:bg-white/[0.05] transition ${
                          depth > 0 ? "border-amber-400/45" : "border-white/10"
                        }`}
                      >
                        <span className="block truncate text-[11px] leading-tight text-slate-400">
                          {depth > 0 && (
                            <span className="mr-0.5 text-amber-300/70">↳</span>
                          )}
                          {q.question || "(untitled)"}
                        </span>
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function PatientSource() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedEvent, setSelectedEvent] = useState("");
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved
  const [exporting, setExporting] = useState(false);
  // Headings collapsed in the document; and which outline groups are expanded.
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());
  const [outlineOpen, setOutlineOpen] = useState(() => new Set());
  // Headings where the user chose to reveal branch-hidden (conditional) fields
  // so they can be filled without first answering the controlling question.
  const [revealedSections, setRevealedSections] = useState(() => new Set());

  // ✅ Load patient data
  useEffect(() => {
    const loadPatient = async () => {
      try {
        const snap = await getDoc(doc(db, getSourcexPatientsPath(), id));
        if (snap.exists()) {
          const raw = snap.data();
          // Normalize in memory only — the stored doc is left untouched until
          // the coordinator actually edits something. Normalization assigns
          // stable question ids deterministically, so a doc created before
          // Phase 0 resolves to exactly the ids its template does.
          let data = normalizePatientData(raw.data);

          // Borrow display-only field structure (table columns, dropdown
          // options, units) from the study template for any field whose
          // snapshot is missing it — so a table always renders and is fillable.
          // Read-only, best-effort: a failure here never blocks the patient.
          try {
            if (raw.studyId) {
              const tSnap = await getDoc(
                doc(db, getSourcexTemplatesPath(), raw.studyId)
              );
              if (tSnap.exists()) {
                data = hydratePatientData(
                  data,
                  normalizeTemplate({ id: tSnap.id, ...tSnap.data() })
                );
              }
            }
          } catch (hydrateErr) {
            console.warn("Could not hydrate field structure from template:", hydrateErr);
          }

          setPatient({ id: snap.id, ...raw, data });
        } else {
          console.warn("❗ No patient found for ID:", id);
        }
      } catch (err) {
        console.error("Error loading patient:", err);
      }
    };
    loadPatient();
  }, [id]);

  // Default the section selector to the first section ("Study Visits" — the only
  // one) once the patient loads, so the user isn't stuck on "Select Section".
  // Guarded so it runs once and never fights a later manual change.
  const didDefaultSection = useRef(false);
  useEffect(() => {
    if (didDefaultSection.current) return;
    const secs = patient?.data || [];
    if (secs.length > 0) {
      didDefaultSection.current = true;
      setSelectedSection((prev) => prev || secs[0].name);
    }
  }, [patient]);

  // Always-current patient, so a stable save callback reads the latest state
  // instead of a stale closure (the callbacks below are memoized for perf).
  const patientRef = useRef(null);
  useEffect(() => {
    patientRef.current = patient;
  }, [patient]);

  // Coalesced background persistence — the same pattern the template editor
  // uses. A keystroke updates local state SYNCHRONOUSLY (so the input reflects
  // it instantly, never gated on the network) and schedules ONE debounced
  // write; a burst of edits costs a single write of the final state. What is
  // written is unchanged — only the timing is coalesced.
  const writeTimer = useRef(null);
  const pendingWrite = useRef(null);

  const flushWrite = useCallback(async () => {
    if (writeTimer.current) {
      clearTimeout(writeTimer.current);
      writeTimer.current = null;
    }
    const data = pendingWrite.current;
    pendingWrite.current = null;
    if (!data) return;
    try {
      await updateDoc(doc(db, getSourcexPatientsPath(), id), data);
      setSaveStatus("saved");
    } catch (e) {
      console.error("Save failed:", e);
      setSaveStatus("idle");
    }
  }, [id]);

  // Optimistic apply: reflect the edit locally now, queue the debounced write.
  const persist = useCallback(
    async (updated) => {
      // A removed patient's source document is retained as a record, not as a
      // working form. Guarding the single choke point every save funnels
      // through means no edit path can write to it — answers, unit toggles,
      // notes or signatures — however it was reached (a bookmarked link, the
      // back button, a stale tab).
      if (patientRef.current?.deletedAt) return;
      patientRef.current = updated;
      setPatient(updated);
      setSaveStatus("saving");
      pendingWrite.current = updated;
      if (writeTimer.current) clearTimeout(writeTimer.current);
      writeTimer.current = setTimeout(() => flushWrite(), 500);
    },
    [flushWrite]
  );

  // Flush any pending write when leaving the page, so a debounced change is
  // never lost on Back/navigate.
  useEffect(() => {
    return () => {
      flushWrite();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Save a question value.
  //
  // Addressed by stable question id, not array index. Branching means the
  // rendered list is a filtered view of event.questions, so a rendered index
  // no longer matches a stored index — writing by index here would put an
  // answer on the wrong question. Also rebuilds the arrays immutably rather
  // than mutating the object held in state, so React sees the change and a
  // failed write can't leave the UI showing an unsaved value as saved.
  const saveValue = useCallback(
    async (sectionName, eventName, questionId, value) => {
    const patient = patientRef.current;
    if (!patient?.data) return;
    const updated = {
      ...patient,
      data: patient.data.map((s) =>
        s.name !== sectionName
          ? s
          : {
              ...s,
              events: (s.events || []).map((e) =>
                e.eventName !== eventName
                  ? e
                  : {
                      ...e,
                      questions: (e.questions || []).map((q) =>
                        q.id === questionId ? { ...q, value } : q
                      ),
                    }
              ),
            }
      ),
    };
    await persist(updated);
    },
    [persist]
  );

  // Metadata edits on a question (currently just the unit toggle on a Number).
  const saveQuestionPatch = useCallback(
    async (sectionName, eventName, questionId, patch) => {
      const patient = patientRef.current;
      if (!patient?.data) return;
      const updated = {
        ...patient,
        data: patient.data.map((s) =>
          s.name !== sectionName
            ? s
            : {
                ...s,
                events: (s.events || []).map((e) =>
                  e.eventName !== eventName
                    ? e
                    : {
                        ...e,
                        questions: (e.questions || []).map((q) =>
                          q.id === questionId ? { ...q, ...patch } : q
                        ),
                      }
                ),
              }
        ),
      };
      await persist(updated);
    },
    [persist]
  );

  // ✅ Save event-level notes
  const saveNotes = useCallback(
    async (sectionName, eventName, value) => {
      const patient = patientRef.current;
      if (!patient?.data) return;
      const updated = {
        ...patient,
        data: patient.data.map((s) =>
          s.name !== sectionName
            ? s
            : {
                ...s,
                events: (s.events || []).map((e) =>
                  e.eventName !== eventName ? e : { ...e, notes: value }
                ),
              }
        ),
      };
      await persist(updated);
    },
    [persist]
  );

  // ✅ Explicit manual save — flush any pending debounced write right now.
  const handleManualSave = async () => {
    if (!patientRef.current) return;
    pendingWrite.current = patientRef.current;
    setSaveStatus("saving");
    await flushWrite();
  };

  // Stable, id-keyed callbacks for the memoized rows. They only change when the
  // active section/event changes (i.e. never while typing), so a row's props
  // stay referentially stable and unchanged fields skip re-rendering.
  const onValue = useCallback(
    (qid, val) => saveValue(selectedSection, selectedEvent, qid, val),
    [saveValue, selectedSection, selectedEvent]
  );
  const onPatch = useCallback(
    (qid, patch) => saveQuestionPatch(selectedSection, selectedEvent, qid, patch),
    [saveQuestionPatch, selectedSection, selectedEvent]
  );

  const sections = patient?.data || [];
  const section = sections.find((s) => s.name === selectedSection);
  const event = section?.events?.find((e) => e.eventName === selectedEvent);

  // Branch-filtered list — the only questions actually shown (an answer on a
  // question later hidden by a branch is retained in the doc, never deleted).
  const shown = useMemo(() => (event ? visibleQuestions(event) : []), [event]);

  // Every heading starts collapsed when a visit opens, so the user sees the
  // form's shape (its sections) before drilling into fields.
  useEffect(() => {
    setOutlineOpen(new Set());
    setRevealedSections(new Set());
    setCollapsedSections(
      new Set(shown.filter((q) => q.type === "Header").map((q) => q.id))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent, selectedSection]);

  const toggleSection = (headingId) =>
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(headingId)) next.delete(headingId);
      else next.add(headingId);
      return next;
    });

  const toggleRevealSection = (headingId) =>
    setRevealedSections((prev) => {
      const next = new Set(prev);
      if (next.has(headingId)) next.delete(headingId);
      else next.add(headingId);
      return next;
    });

  // Toggling a section in the outline toggles it in the document too, so the
  // index and the form never disagree about what's open.
  const toggleOutlineGroup = (key, headingId, willOpen) => {
    setOutlineOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    if (!headingId) return; // the "ungrouped fields" pseudo-group owns nothing
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (willOpen) next.delete(headingId);
      else next.add(headingId);
      return next;
    });
  };

  // Scroll the document to a heading/field, opening its heading first if the
  // target is inside a collapsed section. Pure navigation — edits nothing.
  const jumpToQuestion = (qId) => {
    let owner = null;
    let toOpen = null;
    for (const q of shown) {
      if (q.type === "Header") {
        if (q.id === qId) {
          toOpen = q.id;
          break;
        }
        owner = q.id;
      } else if (q.id === qId) {
        toOpen = owner;
        break;
      }
    }
    if (toOpen) {
      setCollapsedSections((prev) => {
        if (!prev.has(toOpen)) return prev;
        const next = new Set(prev);
        next.delete(toOpen);
        return next;
      });
    }
    requestAnimationFrame(() => {
      const el = document.getElementById(anchorId(qId));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  // Walk the FULL question list once (not just the branch-visible view) so a
  // field hidden by a condition is still accounted for. Per heading we track:
  //  - ownerByQ: which heading owns each field
  //  - sectionFieldCount: branch-visible fields (the collapse badge)
  //  - sectionConditionalCount: fields in the record hidden by a condition
  //  - hiddenByCollapse: fields hidden because their heading is collapsed
  const shownIds = new Set(shown.map((q) => q.id));
  const ownerByQ = new Map();
  const sectionFieldCount = new Map();
  const sectionConditionalCount = new Map();
  const hiddenByCollapse = new Set();
  {
    let owner = null;
    for (const q of event?.questions || []) {
      if (q.type === "Header") {
        owner = q.id;
        sectionFieldCount.set(owner, 0);
        sectionConditionalCount.set(owner, 0);
      } else if (owner) {
        ownerByQ.set(q.id, owner);
        if (collapsedSections.has(owner)) hiddenByCollapse.add(q.id);
        if (!isSourceOnlyType(q.type)) {
          if (shownIds.has(q.id)) {
            sectionFieldCount.set(owner, (sectionFieldCount.get(owner) || 0) + 1);
          } else {
            sectionConditionalCount.set(
              owner,
              (sectionConditionalCount.get(owner) || 0) + 1
            );
          }
        }
      }
    }
  }

  // Visual nesting for conditional fields. Depth comes from the FULL question
  // list so a chain still resolves when a link is currently hidden; the run
  // ends are computed over only what is actually drawn, using the same
  // predicate the render below applies.
  const isRemoved = !!patient?.deletedAt;
  const branchDepth = buildBranchDepth(event?.questions || []);
  // Plain-English rule per conditional field, for the marker's tooltip. Built
  // as strings (not a Map of objects) so the memoized rows still compare equal
  // between renders and typing stays fast.
  const conditionHintById = new Map();
  {
    const labelById = new Map(
      (event?.questions || []).map((q) => [q.id, q.question])
    );
    (event?.questions || []).forEach((q) => {
      if (!q?.visibleWhen) return;
      const d = describeCondition(q.visibleWhen, labelById);
      if (!d.parts.length) return;
      conditionHintById.set(
        q.id,
        `Only shows when ${d.parts.join(d.mode === "all" ? " and " : " or ")}`
      );
    });
  }
  const drawnQuestions = (event?.questions || []).filter((q) => {
    if (q.type === "Header") return true;
    if (hiddenByCollapse.has(q.id)) return false;
    const isConditional = !isSourceOnlyType(q.type) && !shownIds.has(q.id);
    if (!isConditional) return true;
    const owner = ownerByQ.get(q.id);
    return !!owner && revealedSections.has(owner);
  });
  const lastInBranchRun = buildLastInRun(drawnQuestions, branchDepth);

  if (!patient)
    return (
      <div className="sourcex-page relative min-h-screen w-full text-slate-300 font-[Manrope] flex items-center justify-center">
        <PageBackground />
        Loading patient…
      </div>
    );

  return (
    <div className="sourcex-page relative min-h-screen w-full text-slate-100 font-[Manrope] px-4 sm:px-6 py-6">
      <PageBackground />
      <div className="max-w-[1500px] mx-auto flex flex-col gap-4">
        {/* Header */}
        <div className="flex flex-col items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl px-4 py-3">
          <div className="order-2 flex items-center gap-2 min-w-0">
            <FileText className="w-5 h-5 text-purple-300 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-white truncate">{patient.name}</h2>
              <div className="text-[10px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold">
                {patient.studyId}
              </div>
            </div>
          </div>
          <div className="order-1 flex flex-wrap items-center gap-2 shrink-0">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs text-slate-300 hover:text-white transition"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <button
              onClick={() => setExporting(true)}
              title="Export or print this patient's source document with their recorded answers"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs text-slate-300 hover:text-white transition"
            >
              <Printer className="w-3.5 h-3.5" /> Export / Print
            </button>
            {/* Auto-save status */}
            <span
              className={`hidden sm:inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] border ${
                saveStatus === "saving"
                  ? "bg-amber-400/10 border-amber-400/30 text-amber-200"
                  : saveStatus === "saved"
                  ? "bg-emerald-400/10 border-emerald-400/30 text-emerald-200"
                  : "bg-white/[0.04] border-white/10 text-slate-400"
              }`}
            >
              {saveStatus === "saving" ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" /> Saving…
                </>
              ) : saveStatus === "saved" ? (
                <>
                  <CheckCircle2 className="w-3 h-3" /> All changes saved
                </>
              ) : (
                "Auto-save on"
              )}
            </span>
            <button
              onClick={handleManualSave}
              disabled={saveStatus === "saving"}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-xs font-medium text-white hover:brightness-110 disabled:opacity-40 transition"
            >
              <Save className="w-3.5 h-3.5" /> Save
            </button>
          </div>
        </div>

        {/* Section / Event selectors */}
        <div className="flex flex-wrap gap-3 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-3">
          <select
            value={selectedSection}
            onChange={(e) => {
              setSelectedSection(e.target.value);
              setSelectedEvent("");
            }}
            className={`${selectCls} min-w-[200px]`}
          >
            <option value="" className="bg-[#0b0820]">Select Section</option>
            {sections.map((s) => (
              <option key={s.name} value={s.name} className="bg-[#0b0820]">
                {s.name}
              </option>
            ))}
          </select>

          {section && (
            <select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className={`${selectCls} min-w-[200px]`}
            >
              <option value="" className="bg-[#0b0820]">Select Event</option>
              {(section.events || []).map((e) => (
                <option key={e.eventName} value={e.eventName} className="bg-[#0b0820]">
                  {e.eventName}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Removed-patient banner. The record is deliberately still readable —
            it is the source document a monitor may need to see — but nothing
            on it can be edited. */}
        {isRemoved && (
          <div className="mb-4 rounded-2xl border border-amber-400/30 bg-amber-400/[0.08] backdrop-blur-xl px-4 py-3 flex items-start gap-2.5">
            <Trash2 className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-100/90 min-w-0">
              <div className="font-semibold text-amber-200">
                This patient has been removed
              </div>
              <p className="mt-0.5 text-amber-100/80">
                Removed{patient.deletedBy ? ` by ${patient.deletedBy}` : ""}
                {patient.deletedAt
                  ? ` on ${new Date(patient.deletedAt).toLocaleString()}`
                  : ""}
                . The source document is kept as a record and is read-only.
                Restore the patient from the SourceX home page to edit it again.
              </p>
            </div>
          </div>
        )}

        {/* Questions + outline */}
        {event ? (
          <div className="flex flex-col lg:flex-row gap-4 items-start">
            <div className="flex-1 min-w-0 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-4">
              {(event.questions || []).map((q) => {
                if (q.type === "Header") {
                  const isCollapsed = collapsedSections.has(q.id);
                  const owned = sectionFieldCount.get(q.id) || 0;
                  const conditional = sectionConditionalCount.get(q.id) || 0;
                  const revealed = revealedSections.has(q.id);
                  return (
                    <div key={q.id}>
                      <div
                        id={anchorId(q.id)}
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleSection(q.id)}
                        onKeyDown={(ev) => {
                          if (ev.key === "Enter" || ev.key === " ") {
                            ev.preventDefault();
                            toggleSection(q.id);
                          }
                        }}
                        title={isCollapsed ? "Expand this section" : "Collapse this section"}
                        className="scroll-mt-24 mt-4 mb-2 flex items-center gap-1.5 cursor-pointer rounded-lg hover:bg-white/[0.04] transition"
                      >
                        <ChevronRight
                          className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${
                            isCollapsed ? "" : "rotate-90"
                          }`}
                        />
                        <div className="min-w-0 flex-1">
                          <QuestionField question={q} value={q.value} readOnly />
                        </div>
                        {isCollapsed && owned === 0 && conditional > 0 && (
                          <span className="shrink-0 text-[11px] tabular-nums text-amber-200 px-1.5 py-0.5 rounded-md bg-amber-400/10 border border-amber-400/30">
                            {conditional} conditional
                          </span>
                        )}
                        {/* Just the count, pinned right — the word "hidden" was
                            noise next to a chevron that already shows the
                            section is closed. Full wording is the tooltip. */}
                        <span
                          title={
                            isCollapsed && owned > 0
                              ? `${owned} field${owned === 1 ? "" : "s"} hidden`
                              : undefined
                          }
                          className="shrink-0 w-4 text-right text-[11px] tabular-nums text-slate-500"
                        >
                          {isCollapsed && owned > 0 ? owned : ""}
                        </span>
                      </div>

                      {/* Conditional fields (e.g. a table gated by a branch) are
                          in the record but normally appear only once their
                          controlling question is answered. Offer to reveal and
                          fill them directly. */}
                      {!isCollapsed && conditional > 0 && (
                        <button
                          onClick={() => toggleRevealSection(q.id)}
                          className="mb-2 ml-6 inline-flex items-center gap-1.5 text-[11px] text-amber-200 px-2 py-1 rounded-lg bg-amber-400/10 border border-amber-400/30 hover:bg-amber-400/20 transition"
                        >
                          {revealed ? "Hide" : "Show"} {conditional} conditional field
                          {conditional === 1 ? "" : "s"}
                        </button>
                      )}
                    </div>
                  );
                }

                if (hiddenByCollapse.has(q.id)) return null; // section collapsed
                const isConditional =
                  !isSourceOnlyType(q.type) && !shownIds.has(q.id);
                if (isConditional) {
                  // Only render a branch-hidden field when its section is revealed.
                  const owner = ownerByQ.get(q.id);
                  if (!owner || !revealedSections.has(owner)) return null;
                }
                return (
                  <PatientQuestionRow
                    key={q.id}
                    question={q}
                    conditional={isConditional}
                    depth={branchDepth.get(q.id) || 0}
                    lastInRun={lastInBranchRun.get(q.id) === true}
                    conditionHint={conditionHintById.get(q.id) || ""}
                    readOnly={isRemoved}
                    onValue={onValue}
                    onPatch={onPatch}
                  />
                );
              })}

              <div className="mt-6 border-t border-white/10 pt-4">
                <label className="block text-xs uppercase tracking-wider font-medium text-slate-400 mb-1">
                  Notes
                </label>
                <textarea
                  value={event.notes || ""}
                  readOnly={isRemoved}
                  onChange={(e) => saveNotes(selectedSection, selectedEvent, e.target.value)}
                  className={`${inputCls} h-24`}
                  placeholder="Add notes for this event…"
                />
              </div>
            </div>

            {/* Right: document outline — sticky, so it stays put while the form
                scrolls. Hidden on narrow screens where there's no third column. */}
            <aside className="hidden lg:block lg:w-64 xl:w-72 shrink-0">
              <div className="sticky top-4">
                <PatientOutline
                  questions={shown}
                  expanded={outlineOpen}
                  onToggleHeading={toggleOutlineGroup}
                  onJump={jumpToQuestion}
                  branchDepth={branchDepth}
                />
              </div>
            </aside>
          </div>
        ) : (
          <p className="text-slate-500 text-sm">Select a section and event to view questions.</p>
        )}
      </div>

      {/* Export / print — same modal the template page uses, but rendering this
          patient's recorded answers (withAnswers). Defaults to the visit the
          user is viewing. The patient's data is passed as the export source; no
          template document is written. */}
      {exporting && (
        <ExportSourceDocModal
          studyId={patient.studyId}
          template={{ sections: patient.data, sourceStudyId: patient.studyId }}
          defaultEventId={event?.id}
          withAnswers
          patientNumber={patient.screening || patient.randomization || ""}
          patientInitials={initialsFromName(patient.name)}
          onClose={() => setExporting(false)}
        />
      )}
    </div>
  );
}
